import { useState, useEffect } from "react";
import { Navigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { useAuth, hasUserAuth } from "@/lib/auth";
import { FullPageModal } from "@/components/layout/full-page-modal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BillingSection } from "@/components/billing-section";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  KINDS,
  KIND_DETAILS,
  type NotificationChannel,
  type NotificationKind,
} from "@/components/notifications/notification-preferences-dialog";

const sections = [
  { id: "billing", label: "Plan & billing" },
  { id: "profile", label: "Profile" },
  { id: "notifications", label: "Notifications" },
];

function ProfileSection() {
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const { data: profile, isLoading } = trpc.profile.me.useQuery(undefined, {
    enabled: hasUserAuth(auth),
  });

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [initialized, setInitialized] = useState(false);

  // Initialize form state from loaded profile
  if (profile && !initialized) {
    setDisplayName(profile.displayName ?? "");
    setAvatarUrl(profile.avatarUrl ?? "");
    setInitialized(true);
  }

  const updateMutation = trpc.profile.update.useMutation({
    onSuccess: () => {
      toast.success("Profile updated");
      utils.profile.me.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to update profile"),
  });

  function handleSave() {
    const updates: { displayName?: string; avatarUrl?: string } = {};
    const trimmedName = displayName.trim();
    const trimmedAvatar = avatarUrl.trim();

    if (trimmedName && trimmedName !== (profile?.displayName ?? "")) {
      updates.displayName = trimmedName;
    }
    if (trimmedAvatar !== (profile?.avatarUrl ?? "")) {
      updates.avatarUrl = trimmedAvatar || undefined;
    }

    if (Object.keys(updates).length === 0) {
      toast.info("No changes to save");
      return;
    }

    updateMutation.mutate(updates);
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-9 w-full animate-pulse rounded bg-muted" />
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-9 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">Profile</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Your public identity across the workspace.
        </p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={auth?.email ?? ""}
              disabled
              className="bg-muted/50"
            />
            <p className="text-xs text-muted-foreground">
              Email cannot be changed here. Contact support to update your
              email.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display name</Label>
            <Input
              id="displayName"
              placeholder="Jane Doe"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="avatarUrl">Avatar URL</Label>
            <div className="flex items-center gap-3">
              <Input
                id="avatarUrl"
                placeholder="https://example.com/avatar.jpg"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                className="flex-1"
              />
              {avatarUrl.trim() && (
                <img
                  src={avatarUrl.trim()}
                  alt="Avatar preview"
                  className="h-9 w-9 shrink-0 rounded-full object-cover border"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </div>
          </div>

          <div className="pt-2">
            <Button onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function NotificationPreferencesSection() {
  const { auth } = useAuth();
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.notification.preferences.useQuery(
    undefined,
    {
      enabled: hasUserAuth(auth),
    },
  );

  const [channels, setChannels] = useState<
    Record<NotificationKind, NotificationChannel>
  >({
    "finding-assigned": "in-app",
    mentioned: "in-app",
    regressed: "in-app",
    "tracker-conflict": "in-app",
  });
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (data?.preferences && !initialized) {
      const next = { ...channels };
      for (const p of data.preferences) {
        next[p.kind as NotificationKind] = p.channel as NotificationChannel;
      }
      setChannels(next);
      setInitialized(true);
    }
  }, [data, initialized]);

  const updateMutation = trpc.notification.updatePreferences.useMutation({
    onSuccess: () => {
      toast.success("Notification preferences saved");
      utils.notification.preferences.invalidate();
    },
    onError: (err) => {
      toastTrpcError(err, "Failed to save preferences");
    },
  });

  const handleSave = () => {
    const preferences = KINDS.map((kind) => ({
      kind,
      channel: channels[kind],
    }));
    updateMutation.mutate({ preferences });
  };

  if (isLoading && !initialized) {
    return (
      <div className="space-y-3">
        <div className="h-5 w-40 animate-pulse rounded bg-muted" />
        <div className="h-20 w-full animate-pulse rounded bg-muted" />
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          Notification preferences
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure how you receive updates for assigned findings, mentions,
          regressions and tracker conflicts.
        </p>
      </div>
      <Card>
        <CardContent className="pt-4 space-y-4">
          <div className="divide-y divide-border/60">
            {KINDS.map((kind) => {
              const detail = KIND_DETAILS[kind];
              const current = channels[kind];

              return (
                <div
                  key={kind}
                  className="flex items-center justify-between gap-4 py-3 first:pt-1 last:pb-1"
                >
                  <div className="space-y-0.5 min-w-0 pr-2">
                    <Label className="text-sm font-medium leading-none text-foreground">
                      {detail.label}
                    </Label>
                    <p className="text-xs text-muted-foreground leading-normal mt-1">
                      {detail.description}
                    </p>
                  </div>
                  <div className="shrink-0 w-[160px]">
                    <Select
                      value={current}
                      onValueChange={(val: NotificationChannel) =>
                        setChannels((prev) => ({ ...prev, [kind]: val }))
                      }
                      disabled={isLoading || updateMutation.isPending}
                    >
                      <SelectTrigger className="h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in-app">In-app only</SelectItem>
                        <SelectItem value="in-app+email">
                          In-app + Email
                        </SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSave}
              disabled={isLoading || updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save preferences"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

const WEB_APP_URL =
  (import.meta.env.VITE_WEB_APP_URL as string | undefined) ||
  "https://iamu.dev";

export function AccountPage() {
  const { auth } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const hashSection = location.hash?.replace("#", "").replace("section-", "");

  const resolvedSection =
    (tabParam && sections.some((s) => s.id === tabParam) ? tabParam : null) ??
    (hashSection && sections.some((s) => s.id === hashSection)
      ? hashSection
      : null) ??
    "billing";

  const [activeSection, setActiveSection] = useState(resolvedSection);

  useEffect(() => {
    setActiveSection(resolvedSection);
  }, [resolvedSection]);

  const handleSelectSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const next = new URLSearchParams(searchParams);
    next.set("tab", sectionId);
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (auth && !hasUserAuth(auth)) {
    return <Navigate to="/settings" replace />;
  }

  const currentSection =
    sections.find((s) => s.id === activeSection) ?? sections[0];

  return (
    <FullPageModal
      title="Account"
      subtitle="Manage your profile, notifications, and billing"
      onClose={() => window.history.back()}
      stickyHeader={true}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-8">
        <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-12">
          {/* Mobile section selector */}
          <div className="mb-6 lg:hidden">
            <Label htmlFor="mobile-account-section" className="sr-only">
              Account section
            </Label>
            <Select
              value={currentSection.id}
              onValueChange={handleSelectSection}
            >
              <SelectTrigger id="mobile-account-section" className="w-full">
                <SelectValue placeholder="Select section" />
              </SelectTrigger>
              <SelectContent>
                {sections.map((section) => (
                  <SelectItem key={section.id} value={section.id}>
                    {section.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop left navigation */}
          <aside className="hidden lg:block">
            <nav
              aria-label="Account sections"
              className="sticky top-24 space-y-6"
            >
              <ul className="space-y-0.5">
                {sections.map((section) => {
                  const isActive = currentSection.id === section.id;
                  return (
                    <li key={section.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectSection(section.id)}
                        aria-current={isActive ? "page" : undefined}
                        className={cn(
                          "w-full rounded-md px-2.5 py-1.5 text-left text-sm transition-colors",
                          isActive
                            ? "bg-foreground text-background font-semibold shadow-xs"
                            : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                        )}
                      >
                        {section.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2.5 pt-2 text-xs text-muted-foreground">
                <a
                  href={`${WEB_APP_URL}/privacy`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap hover:text-foreground"
                >
                  Privacy Policy
                </a>
                <span aria-hidden="true">&middot;</span>
                <a
                  href={`${WEB_APP_URL}/terms`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap hover:text-foreground"
                >
                  Terms of Service
                </a>
              </div>
            </nav>
          </aside>

          {/* Main content area — renders active section */}
          <main className="min-w-0 max-w-3xl">
            <div id={`section-${currentSection.id}`} className="scroll-mt-24">
              {currentSection.id === "billing" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      Plans and limits apply to your active workspace.
                    </span>
                    <Link
                      to="/settings?tab=billing"
                      className="text-primary hover:underline font-medium"
                    >
                      Open Workspace Settings &rarr;
                    </Link>
                  </div>
                  <BillingSection />
                </div>
              )}
              {currentSection.id === "profile" && <ProfileSection />}
              {currentSection.id === "notifications" && (
                <NotificationPreferencesSection />
              )}
            </div>
          </main>
        </div>
      </div>
    </FullPageModal>
  );
}
